import { useEffect, useState } from "react";
import { GraphQLError } from "graphql";
import { merge } from "lodash";
import { useSegment } from "analytics/segment";
import {
  CreateAttachmentAttachment,
  useCreateAttachment,
} from "core/queries/CreateAttachment";
import { FileUploadAttachments } from "tasks/actions";
import { UploadFileCategory, useUploadFiles } from "tasks/hooks/useUploadFiles";
import {
  Attachable,
  Format,
  TaskFilterInput,
  UserFilterInput,
} from "typings/globalGQLTypes";

// When tracking filter button/checkbox clicks, we need to wait for the
// `globalFilters` state to be updated before sending our Segment beacons. This
// hook is a wrapper around `useSegment` that let's us queue up tracking beacons
// while we wait for state to update.
export const useSegmentQueue = (
  globalFilters: TaskFilterInput | UserFilterInput,
) => {
  const { track } = useSegment();
  const [state, setState] = useState<SegmentEvent[]>([]);

  useEffect(() => {
    if (state.length > 0) {
      state
        .map(event => merge(event, { commonProps: { filters: globalFilters } }))
        .forEach(track);

      setState([]);
    }
  }, [state, globalFilters]);

  return (event: SegmentEvent) =>
    setState(prevState => prevState.concat(event));
};

export const useUploadAndAttachFiles = ({
  awaitRefetchQueries = true,
  refetchQueries = [],
}) => {
  const [onCreateAttachment, { loading: isAttaching }] = useCreateAttachment({
    awaitRefetchQueries,
    refetchQueries,
  });
  const {
    onUploadFiles,
    uploadState: { loading: isUploading, data: uploadData, error: uploadError },
  } = useUploadFiles();
  const [attachData, setAttachData] = useState<CreateAttachmentAttachment[]>(
    [],
  );
  const [attachError, setAttachError] = useState<GraphQLError[]>([]);
  return {
    isAttaching,
    isUploading,
    data: {
      upload: uploadData,
      attach: attachData,
    },
    error: {
      upload: uploadError,
      attach: attachError,
    },
    onUploadAndAttach: async ({
      files,
      attachableId,
    }: FileUploadAttachments) => {
      if (attachableId && files.length) {
        setAttachData([]);
        setAttachError([]);
        // Upload files
        const { data } = await onUploadFiles({
          category: UploadFileCategory.TaskAttachment,
          files,
        });
        if (data?.success && data?.uploadedFiles?.length)
          return Promise.all(
            data?.uploadedFiles?.map(f =>
              onCreateAttachment({
                variables: {
                  attachToNewComment: false,
                  markAllRead: false,
                  targetId: attachableId,
                  targetType: Attachable.task,
                  format: f.format as Format,
                  publicId: f.publicId,
                },
              }).then(resp => {
                const newAttachment = resp.data?.createAttachment?.attachment;
                const newErrors = resp.errors || [];
                if (newAttachment)
                  setAttachData(state => (state || []).concat(newAttachment));

                if (newErrors.length)
                  setAttachError(state => [...(state || []), ...newErrors]);

                return resp;
              }),
            ),
          );

        return null;
      }
      return null;
    },
  };
};
